import { EMAIL_CATALOG } from './catalog';
import type { EmailBlockAttributes, EmailBlockType, EmailRenderContext } from './catalog';
import type { PersistedEmailBlock } from './useEmailBlockTree';

/**
 * Seeding + mapping for the visual email builder.
 *
 * When a merchant first opens the builder for an email that has no stored block list, we seed the
 * flat block stack from the current `formValues` + selected template, reproducing the exact order
 * the legacy `generateEmailTemplate` produced (including the special blocks it spliced in by string
 * surgery). The same `BLOCK_ATTR_TO_FORM_VALUE` table drives seeding and inspector write-through, so
 * editing a block in the builder keeps the legacy flat `formValues` in sync (dirty-tracking +
 * "save styling as default for all" keep working).
 */

type FormValues = Record<string, unknown>;

/**
 * Maps `${blockType}.${attr}` to the flat `formValues` key it mirrors. Used to (a) seed a block's
 * attributes from `formValues` and (b) write inspector edits back to `formValues`. Blocks/attrs not
 * listed here (divider, spacer, image, reviews, …) have no flat-form equivalent and live only in the
 * persisted block list.
 */
export const BLOCK_ATTR_TO_FORM_VALUE: Record<string, string> = {
  'button.background': 'buttonBackground',
  'button.borderRadius': 'buttonBorderRadius',
  'button.fontColor': 'buttonFontColor',
  'button.fontFamily': 'paragraphDesktopFontFamily',
  'button.fontStyle': 'buttonFontStyle',
  'button.fontWeight': 'paragraphDesktopFontWeight',
  'button.link': 'buttonLink',
  'button.text': 'buttonText',

  'footer.footerContent': 'footerContent',
  'footer.togglePoweredByOpEnabled': 'togglePoweredByOpEnabled',

  'heading.fontColor': 'headlineDesktopFontColor',
  'heading.fontFamily': 'headlineDesktopFontFamily',
  'heading.fontSize': 'headlineDesktopFontSize',
  'heading.fontStyle': 'headlineFontStyle',
  'heading.fontWeight': 'headlineDesktopFontWeight',
  'heading.letterSpacing': 'headlineDesktopLetterSpacing',
  'heading.lineHeight': 'headlineDesktopLineHeight',
  'heading.text': 'headline',

  'icon.iconUrl': 'iconUrl',

  'logo.desktopLogoWidth': 'desktopLogoWidth',
  'logo.logoUrl': 'logoUrl',

  'paragraph.fontColor': 'paragraphDesktopFontColor',
  'paragraph.fontFamily': 'paragraphDesktopFontFamily',
  'paragraph.fontSize': 'paragraphDesktopFontSize',
  'paragraph.fontStyle': 'paragraphFontStyle',
  'paragraph.fontWeight': 'paragraphDesktopFontWeight',
  'paragraph.letterSpacing': 'paragraphDesktopLetterSpacing',
  'paragraph.lineHeight': 'paragraphDesktopLineHeight',
  'paragraph.text': 'paragraph',

  'storeCreditValue.background': 'buttonBackground',
  'storeCreditValue.borderRadius': 'buttonBorderRadius',
  'storeCreditValue.fontColor': 'buttonFontColor',
  'storeCreditValue.fontFamily': 'paragraphDesktopFontFamily',
  'storeCreditValue.fontStyle': 'buttonFontStyle',
  'storeCreditValue.fontWeight': 'paragraphDesktopFontWeight',
  'storeCreditValue.link': 'buttonLink',
  'storeCreditValue.text': 'buttonText',

  'subtext.fontColor': 'subTextDesktopFontColor',
  'subtext.fontFamily': 'subTextDesktopFontFamily',
  'subtext.fontSize': 'subTextDesktopFontSize',
  'subtext.fontStyle': 'subTextDesktopFontStyle',
  'subtext.fontWeight': 'subTextDesktopFontWeight',
  'subtext.letterSpacing': 'subTextDesktopLetterSpacing',
  'subtext.lineHeight': 'subTextDesktopLineHeight',
  'subtext.text': 'subText',
};

/** Returns the flat `formValues` key a block attribute mirrors, or null when it has none. */
export function mapBlockAttrToFormValue(type: EmailBlockType, attr: string): string | null {
  return BLOCK_ATTR_TO_FORM_VALUE[`${type}.${attr}`] ?? null;
}

/** Builds a block's attributes: catalog defaults overlaid with any matching `formValues` values. */
function seedAttributes(type: EmailBlockType, formValues: FormValues): EmailBlockAttributes {
  const attrs: EmailBlockAttributes = { ...EMAIL_CATALOG[type].defaultAttributes };

  for (const [combinedKey, fvKey] of Object.entries(BLOCK_ATTR_TO_FORM_VALUE)) {
    // Split on the first dot only, so an attribute name containing a dot keeps its full tail.
    const dot = combinedKey.indexOf('.');
    const blockType = combinedKey.slice(0, dot);
    const attr = combinedKey.slice(dot + 1);
    const value = formValues[fvKey];
    if (blockType === type && value !== undefined && value !== null) {
      attrs[attr] = value as string | number | boolean;
    }
  }

  return attrs;
}

/**
 * Seeds the flat block stack from `formValues` + `templateKey`, reproducing the legacy
 * `generateEmailTemplate` block order:
 *   header[logo, (brandName)] → [icon] → heading → [unreadMessage] → paragraph → [subtext]
 *        → button | storeCreditValue → [reviews | warrantyProducts | unreadPostText] → footer
 */
export function seedBlocksFromFormValues(formValues: FormValues, templateKey: string): PersistedEmailBlock[] {
  const make = (type: EmailBlockType): PersistedEmailBlock => ({ attributes: seedAttributes(type, formValues), type });
  const blocks: PersistedEmailBlock[] = [];

  // Logo image + brand-name text grouped side by side in a header row. The brand name only seeds when
  // there's no logo image, matching the legacy template (which hid the name once a logo was uploaded).
  const headerChildren: PersistedEmailBlock[] = [make('logo')];
  if (!formValues.logoUrl) headerChildren.push(make('brandName'));
  blocks.push({ attributes: {}, children: headerChildren, type: 'header' });

  const iconUrl = formValues.iconUrl;
  if (iconUrl && iconUrl !== formValues.logoUrl) blocks.push(make('icon'));

  blocks.push(make('heading'));

  if (templateKey === 'unread') blocks.push(make('unreadMessage'));

  blocks.push(make('paragraph'));

  if (formValues.subText) blocks.push(make('subtext'));

  // CTA: the store-credit box owns its own button, so there is no standalone button for that template.
  blocks.push(templateKey === 'storeCredit' ? make('storeCreditValue') : make('button'));

  // Template-specific blocks the legacy compiler spliced in just before the footer.
  if (templateKey === 'approved') blocks.push(make('reviews'));
  if (templateKey === 'extendedWarranty') blocks.push(make('warrantyProducts'));
  if (templateKey === 'unread') blocks.push(make('unreadPostText'));

  blocks.push(make('footer'));

  return blocks;
}

/** Context shared by all block render functions. */
export function buildRenderContext(formValues: FormValues, storeName: string, isPreview: boolean): EmailRenderContext {
  return {
    isPreview,
    storeName: String(formValues.storeName || storeName || 'BrandName'),
  };
}

/** The values the document `<head>` (`:root` CSS vars + mobile logo width) needs. Passthrough of formValues. */
export interface EmailDocumentVars {
  headlineDesktopFontColor: string;
  headlineDesktopFontFamily: string;
  headlineDesktopFontSize: number;
  headlineDesktopFontWeight: number | string;
  headlineMobileFontColor: string;
  headlineMobileFontFamily: string;
  headlineMobileFontSize: number;
  headlineMobileFontWeight: number | string;
  paragraphDesktopFontColor: string;
  paragraphDesktopFontFamily: string;
  paragraphDesktopFontSize: number;
  paragraphDesktopFontWeight: number | string;
  paragraphMobileFontColor: string;
  paragraphMobileFontFamily: string;
  paragraphMobileFontSize: number;
  paragraphMobileFontWeight: number | string;
  mobileLogoWidth: number;
}

const DOCUMENT_VAR_KEYS: (keyof EmailDocumentVars)[] = [
  'headlineDesktopFontColor',
  'headlineDesktopFontFamily',
  'headlineDesktopFontSize',
  'headlineDesktopFontWeight',
  'headlineMobileFontColor',
  'headlineMobileFontFamily',
  'headlineMobileFontSize',
  'headlineMobileFontWeight',
  'paragraphDesktopFontColor',
  'paragraphDesktopFontFamily',
  'paragraphDesktopFontSize',
  'paragraphDesktopFontWeight',
  'paragraphMobileFontColor',
  'paragraphMobileFontFamily',
  'paragraphMobileFontSize',
  'paragraphMobileFontWeight',
  'mobileLogoWidth',
];

/** Picks the document-level styling vars from `formValues` for `wrapEmailDocument`. */
export function buildDocumentVars(formValues: FormValues): EmailDocumentVars {
  const vars = {} as Record<keyof EmailDocumentVars, unknown>;
  for (const key of DOCUMENT_VAR_KEYS) vars[key] = formValues[key];

  return vars as unknown as EmailDocumentVars;
}
